import React, { useEffect, useState, useRef } from "react";
import { TopAdminNav } from "../layouts/TopAdminNav";
import {
  FileText,
  Plus,
  Filter,
  Search,
  CheckCircle2,
  Clock,
  Edit3,
  ShieldCheck,
  Image as ImageIcon,
  Check,
  X,
  User,
  ChevronDown,
  Loader2,
  BookOpen,
  Trash2,
  ShoppingBag,
  AlertTriangle,
  CreditCard,
  UploadCloud,
  KeyRound,
  EyeOff,
  Eye,
} from "lucide-react";
import {
  uploadImageToCloudinary,
  uploadAccept,
  validateUploadFile,
} from "../utilities/importantFns";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import Pagination from "../components/pagination";
import { IPaginationParams } from "../types/interfaces";
import {
  InvoiceItem,
  getAllInvoices,
  deleteInvoice,
  searchStudentsServerSide,
  getClassrooms,
  PaymentVerificationItem,
  getPaymentVerifications,
  StationeryItem,
  getStationeries,
  deleteStationery,
  rejectPaymentVerification,
  postPaymentOnBehalf,
  getStudentInvoices,
  setTransactionPin,
} from "../services/admin";
import { makeFetchRequest } from "../utilities/common";
import { defaultUrl } from "../features/actions";
import { InvoiceModal } from "../components/invoices/InvoiceModal";
import { StationeryModal } from "../components/invoices/StationeryModal";
import { PaymentVerificationModal } from "../components/invoices/PaymentVerificationModal";
import { generateAcademicSessions } from "../utilities/academicSession";
import { useDebounce } from "../hooks/useDebounce";

export const InvoiceManagement: React.FC = () => {
  const ITEMS_PER_PAGE = 10;
  const user = JSON.parse((sessionStorage.getItem("user") as string) || "{}");
  const isSuperAdmin = user?.role === "super_admin";

  // Active Tab: "invoices" | "stationeries" | "verifications"
  const [activeTab, setActiveTab] = useState<
    "invoices" | "stationeries" | "verifications"
  >("invoices");

  // Invoices State
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(true);
  const [invoiceSummary, setInvoiceSummary] = useState<Record<
    string,
    any
  > | null>(null);
  const [studentList, setStudentList] = useState<any[]>([]);
  const [rawStudentsWithParents, setRawStudentsWithParents] = useState<any[]>(
    [],
  );
  const [invoicePage, setInvoicePage] = useState<number>(1);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState<number>(1);
  const [invoicePaginationParams, setInvoicePaginationParams] =
    useState<IPaginationParams>({
      page: 1,
      totalPages: 1,
      totalItems: 0,
      from: 0,
      to: 0,
    });

  // Stationeries State (Table vs Unpaginated Select Field)
  const [stationeriesTable, setStationeriesTable] = useState<StationeryItem[]>(
    [],
  );
  const [allStationeriesForSelect, setAllStationeriesForSelect] = useState<
    StationeryItem[]
  >([]);
  const [loadingStationeries, setLoadingStationeries] =
    useState<boolean>(false);
  const [stationerySearchTerm, setStationerySearchTerm] = useState<string>("");
  const [isStationeryModalOpen, setIsStationeryModalOpen] =
    useState<boolean>(false);
  const [editingStationery, setEditingStationery] =
    useState<StationeryItem | null>(null);
  const [preselectedStationeryId, setPreselectedStationeryId] = useState<
    string | null
  >(null);
  const [stationeryPage, setStationeryPage] = useState<number>(1);
  const [stationeryTotalPages, setStationeryTotalPages] = useState<number>(1);
  const [stationeryPaginationParams, setStationeryPaginationParams] =
    useState<IPaginationParams>({
      page: 1,
      totalPages: 1,
      totalItems: 0,
      from: 0,
      to: 0,
    });

  // Verifications State
  const [verifications, setVerifications] = useState<PaymentVerificationItem[]>(
    [],
  );
  const [loadingVerifications, setLoadingVerifications] =
    useState<boolean>(false);
  const [verificationStatusFilter, setVerificationStatusFilter] =
    useState<string>("pending");
  const [verificationSearchTerm, setVerificationSearchTerm] =
    useState<string>("");
  const [selectedVerification, setSelectedVerification] =
    useState<PaymentVerificationItem | null>(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] =
    useState<boolean>(false);
  const [verificationDefaultAction, setVerificationDefaultAction] = useState<
    "approve" | "reject" | null
  >(null);
  const [isVerificationRejectModalOpen, setIsVerificationRejectModalOpen] =
    useState<boolean>(false);
  const [tableRejectionReason, setTableRejectionReason] = useState<string>("");
  const [isRejecting, setIsRejecting] = useState<boolean>(false);
  const [verificationPage, setVerificationPage] = useState<number>(1);
  const [verificationTotalPages, setVerificationTotalPages] =
    useState<number>(1);
  const [verificationPaginationParams, setVerificationPaginationParams] =
    useState<IPaginationParams>({
      page: 1,
      totalPages: 1,
      totalItems: 0,
      from: 0,
      to: 0,
    });

  // Shared session/term filters — persist across all tabs
  const [sessionFilter, setSessionFilter] = useState<string>("2026/2027");
  const [termFilter, setTermFilter] = useState<string>("");

  // Aliases so both tab code paths refer to the same state
  const invoiceSessionFilter = sessionFilter;
  const invoiceTermFilter = termFilter;
  const setInvoiceSessionFilter = setSessionFilter;
  const setInvoiceTermFilter = setTermFilter;
  const verificationSessionFilter = sessionFilter;
  const verificationTermFilter = termFilter;
  const setVerificationSessionFilter = setSessionFilter;
  const setVerificationTermFilter = setTermFilter;
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [selectedClassFilter, setSelectedClassFilter] = useState<string>("");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState<string>("");
  const [selectedStudentFilterObj, setSelectedStudentFilterObj] = useState<
    any | null
  >(null);
  const [isStudentFilterDropdownOpen, setIsStudentFilterDropdownOpen] =
    useState<boolean>(false);
  const [studentFilterSearchQuery, setStudentFilterSearchQuery] =
    useState<string>("");
  const debouncedStudentFilterSearch = useDebounce(
    studentFilterSearchQuery,
    400,
  );
  const [studentFilterList, setStudentFilterList] = useState<any[]>([]);
  const [fetchingFilterStudents, setFetchingFilterStudents] =
    useState<boolean>(false);
  const studentFilterDropdownRef = useRef<HTMLDivElement>(null);

  // Student & Class filter dropdowns for Payment Verifications Table
  const [selectedVerificationClassFilter, setSelectedVerificationClassFilter] =
    useState<string>("");
  const [isVerificationFilterPanelOpen, setIsVerificationFilterPanelOpen] =
    useState<boolean>(true);
  const [
    selectedVerificationStudentFilter,
    setSelectedVerificationStudentFilter,
  ] = useState<string>("");
  const [
    selectedVerificationStudentFilterObj,
    setSelectedVerificationStudentFilterObj,
  ] = useState<any | null>(null);
  const [
    isVerificationStudentFilterDropdownOpen,
    setIsVerificationStudentFilterDropdownOpen,
  ] = useState<boolean>(false);
  const [
    verificationStudentFilterSearchQuery,
    setVerificationStudentFilterSearchQuery,
  ] = useState<string>("");
  const debouncedVerificationStudentFilterSearch = useDebounce(
    verificationStudentFilterSearchQuery,
    400,
  );
  const [verificationStudentFilterList, setVerificationStudentFilterList] =
    useState<any[]>([]);
  const [
    fetchingVerificationFilterStudents,
    setFetchingVerificationFilterStudents,
  ] = useState<boolean>(false);
  const verificationStudentFilterDropdownRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");

  // On-Behalf Payment Modal State (superadmin only)
  const [isOnBehalfModalOpen, setIsOnBehalfModalOpen] =
    useState<boolean>(false);
  // Student server-side search for on-behalf modal
  const [isOnBehalfStudentDropdownOpen, setIsOnBehalfStudentDropdownOpen] =
    useState<boolean>(false);
  const [onBehalfStudentSearchQuery, setOnBehalfStudentSearchQuery] =
    useState<string>("");
  const debouncedOnBehalfStudentSearch = useDebounce(
    onBehalfStudentSearchQuery,
    400,
  );
  const [onBehalfStudentList, setOnBehalfStudentList] = useState<any[]>([]);
  const [fetchingOnBehalfStudents, setFetchingOnBehalfStudents] =
    useState<boolean>(false);
  const [onBehalfSelectedStudentId, setOnBehalfSelectedStudentId] =
    useState<string>("");
  const [onBehalfSelectedStudentObj, setOnBehalfSelectedStudentObj] = useState<
    any | null
  >(null);
  const onBehalfStudentDropdownRef = useRef<HTMLDivElement>(null);
  // Invoices for selected student
  const [onBehalfStudentInvoices, setOnBehalfStudentInvoices] = useState<
    InvoiceItem[]
  >([]);
  const [loadingOnBehalfInvoices, setLoadingOnBehalfInvoices] =
    useState<boolean>(false);
  // Proof image upload
  const [onBehalfProofFile, setOnBehalfProofFile] = useState<File | null>(null);
  const [onBehalfProofPreview, setOnBehalfProofPreview] = useState<string>("");
  const [uploadingProof, setUploadingProof] = useState<boolean>(false);
  const [onBehalfForm, setOnBehalfForm] = useState({
    invoice_id: "",
    parent_id: "",
    amount_paid: "",
    payment_method: "transfer",
    admin_notes: "",
    transaction_pin: "",
  });
  const [isPostingOnBehalf, setIsPostingOnBehalf] = useState<boolean>(false);

  // Set Transaction PIN popup state
  const [isSetPinModalOpen, setIsSetPinModalOpen] = useState<boolean>(false);
  const [setPinForm, setSetPinForm] = useState({
    current_password: "",
    transaction_pin: "",
    transaction_pin_confirmation: "",
  });
  const [isSettingPin, setIsSettingPin] = useState<boolean>(false);
  const [showSetPinPassword, setShowSetPinPassword] = useState<boolean>(false);

  // Invoice Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceItem | null>(
    null,
  );
  const [viewInvoice, setViewInvoice] = useState<InvoiceItem | null>(null);

  // Debounced search terms
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedStationerySearchTerm = useDebounce(stationerySearchTerm, 500);
  const debouncedVerificationSearchTerm = useDebounce(
    verificationSearchTerm,
    500,
  );

  // Server-side Search for Student Filter in Invoice Table (first 10 items or matching query)
  useEffect(() => {
    const loadFilterStudents = async () => {
      setFetchingFilterStudents(true);
      try {
        const results = await searchStudentsServerSide(
          debouncedStudentFilterSearch,
          1,
          10,
        );
        setStudentFilterList(results);
      } catch (err) {
        console.error("Error loading server-side students for filter:", err);
      } finally {
        setFetchingFilterStudents(false);
      }
    };
    loadFilterStudents();
  }, [debouncedStudentFilterSearch]);

  // Server-side Search for Student Filter in Payment Verification Table
  useEffect(() => {
    const loadVerificationFilterStudents = async () => {
      setFetchingVerificationFilterStudents(true);
      try {
        const results = await searchStudentsServerSide(
          debouncedVerificationStudentFilterSearch,
          1,
          10,
        );
        setVerificationStudentFilterList(results);
      } catch (err) {
        console.error(
          "Error loading server-side students for verification filter:",
          err,
        );
      } finally {
        setFetchingVerificationFilterStudents(false);
      }
    };
    loadVerificationFilterStudents();
  }, [debouncedVerificationStudentFilterSearch]);

  // Server-side search for on-behalf student dropdown
  useEffect(() => {
    const load = async () => {
      setFetchingOnBehalfStudents(true);
      try {
        const results = await searchStudentsServerSide(
          debouncedOnBehalfStudentSearch,
          1,
          10,
        );
        setOnBehalfStudentList(results);
      } catch {
        // silent
      } finally {
        setFetchingOnBehalfStudents(false);
      }
    };
    load();
  }, [debouncedOnBehalfStudentSearch]);

  // Click outside listener for student filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        studentFilterDropdownRef.current &&
        !studentFilterDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStudentFilterDropdownOpen(false);
      }
      if (
        verificationStudentFilterDropdownRef.current &&
        !verificationStudentFilterDropdownRef.current.contains(
          event.target as Node,
        )
      ) {
        setIsVerificationStudentFilterDropdownOpen(false);
      }
      if (
        onBehalfStudentDropdownRef.current &&
        !onBehalfStudentDropdownRef.current.contains(event.target as Node)
      ) {
        setIsOnBehalfStudentDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Unpaginated fetch for Students select field
  const fetchStudentsForSelect = async () => {
    try {
      const res = await makeFetchRequest(
        `${defaultUrl}/students/withoutPagination`,
      );
      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      const formatted = rawArray.map((st: any) => {
        const fullName = [st.firstname, st.surname, st.othername]
          .filter(Boolean)
          .join(" ");
        return {
          id: st.id,
          name: fullName || st.name || `Student #${st.id.substring(0, 6)}`,
          email: st.email,
          classroom: st.classroom
            ? { id: st.classroom.id, name: st.classroom.name }
            : undefined,
        };
      });
      setStudentList(formatted);
      setRawStudentsWithParents(
        rawArray.map((st: any) => {
          const fullName = [st.firstname, st.surname, st.othername]
            .filter(Boolean)
            .join(" ");
          return {
            ...st,
            _displayName:
              fullName || st.name || `Student #${st.id.substring(0, 6)}`,
          };
        }),
      );
    } catch (e) {
      console.error("Error fetching unpaginated students for select:", e);
    }
  };

  // Unpaginated fetch for Classrooms select field
  const fetchClassroomsForSelect = async () => {
    try {
      const res = await getClassrooms();
      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
      setClassrooms(rawArray);
    } catch (e) {
      console.error("Error fetching classrooms for select:", e);
    }
  };

  // Unpaginated fetch for Stationeries select field
  const fetchAllStationeriesForSelect = async () => {
    try {
      // Called WITHOUT pagination params so ALL stationeries are returned for select field
      const res = await getStationeries();
      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setAllStationeriesForSelect(rawArray);
    } catch (error) {
      console.error(
        "Error fetching unpaginated stationeries for select:",
        error,
      );
    }
  };

  // Server-side Paginated fetch for Stationeries Catalog Table
  const fetchStationeriesTable = async () => {
    setLoadingStationeries(true);
    try {
      const res = await getStationeries({
        search: debouncedStationerySearchTerm || undefined,
        page: stationeryPage,
        per_page: ITEMS_PER_PAGE,
      });

      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];

      const meta = res?.data?.meta || res?.meta || res?.data;

      setStationeriesTable(rawArray);

      if (
        meta &&
        typeof meta === "object" &&
        (meta.total !== undefined || meta.last_page !== undefined)
      ) {
        const totalItems =
          meta.total !== undefined ? meta.total : rawArray.length;
        const totalPages =
          meta.last_page !== undefined
            ? meta.last_page
            : Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from =
          meta.from !== undefined
            ? meta.from
            : totalItems
              ? (stationeryPage - 1) * ITEMS_PER_PAGE + 1
              : 0;
        const to =
          meta.to !== undefined
            ? meta.to
            : totalItems
              ? Math.min(stationeryPage * ITEMS_PER_PAGE, totalItems)
              : 0;

        setStationeryPaginationParams({
          page: meta.current_page || stationeryPage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setStationeryTotalPages(totalPages);
      } else {
        const totalItems = rawArray.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from = totalItems ? (stationeryPage - 1) * ITEMS_PER_PAGE + 1 : 0;
        const to = totalItems
          ? Math.min(stationeryPage * ITEMS_PER_PAGE, totalItems)
          : 0;

        setStationeryPaginationParams({
          page: stationeryPage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setStationeryTotalPages(totalPages);
      }
    } catch (error) {
      console.error("Error fetching stationeries table:", error);
      setStationeriesTable([]);
    } finally {
      setLoadingStationeries(false);
    }
  };

  // Server-side Paginated fetch with unified 'q' (Class ID, Student ID, or Invoice Code)
  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      let qValue: string | undefined = undefined;
      const cleanSearch = debouncedSearchTerm ? debouncedSearchTerm.trim() : "";

      if (cleanSearch) {
        qValue = cleanSearch;
      } else if (selectedStudentFilter) {
        qValue = selectedStudentFilter;
      } else if (selectedClassFilter) {
        qValue = selectedClassFilter;
      }

      const res = await getAllInvoices({
        academic_session: invoiceSessionFilter || undefined,
        term: invoiceTermFilter || undefined,
        q: qValue,
        page: invoicePage,
        per_page: ITEMS_PER_PAGE,
      });

      let rawArray: InvoiceItem[] = [];
      if (Array.isArray(res?.data)) {
        rawArray = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        rawArray = res.data.data;
      } else if (Array.isArray(res)) {
        rawArray = res;
      } else if (
        res?.data &&
        typeof res.data === "object" &&
        (res.data.id || res.data.invoice_code)
      ) {
        rawArray = [res.data];
      }

      // Extract summary analytics object from backend response
      const rawSummary =
        res?.summary ||
        res?.data?.summary ||
        res?.meta?.summary ||
        res?.stats ||
        res?.data?.stats ||
        null;
      if (rawSummary && typeof rawSummary === "object") {
        setInvoiceSummary(rawSummary);
      } else {
        setInvoiceSummary(null);
      }

      const meta = res?.meta || res?.data?.meta || res?.data;

      setInvoices(rawArray);

      if (
        meta &&
        typeof meta === "object" &&
        (meta.total !== undefined || meta.last_page !== undefined)
      ) {
        const totalItems =
          meta.total !== undefined ? Number(meta.total) : rawArray.length;
        const totalPages =
          meta.last_page !== undefined
            ? Number(meta.last_page)
            : Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from =
          meta.from !== undefined
            ? Number(meta.from)
            : totalItems
              ? (invoicePage - 1) * ITEMS_PER_PAGE + 1
              : 0;
        const to =
          meta.to !== undefined
            ? Number(meta.to)
            : totalItems
              ? Math.min(invoicePage * ITEMS_PER_PAGE, totalItems)
              : 0;

        setInvoicePaginationParams({
          page: meta.current_page ? Number(meta.current_page) : invoicePage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setInvoiceTotalPages(totalPages);
      } else {
        const totalItems = rawArray.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from = totalItems ? (invoicePage - 1) * ITEMS_PER_PAGE + 1 : 0;
        const to = totalItems
          ? Math.min(invoicePage * ITEMS_PER_PAGE, totalItems)
          : 0;

        setInvoicePaginationParams({
          page: invoicePage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setInvoiceTotalPages(totalPages);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
      setInvoicePaginationParams({
        page: 1,
        totalPages: 1,
        from: 0,
        to: 0,
        totalItems: 0,
      });
      setInvoiceTotalPages(1);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Server-side Paginated fetch for Payment Verifications Table
  const fetchVerifications = async () => {
    setLoadingVerifications(true);
    try {
      let qValue: string | undefined = undefined;
      const cleanSearch = debouncedVerificationSearchTerm
        ? debouncedVerificationSearchTerm.trim()
        : "";

      if (cleanSearch) {
        qValue = cleanSearch;
      } else if (selectedVerificationStudentFilter) {
        qValue = selectedVerificationStudentFilter;
      } else if (selectedVerificationClassFilter) {
        qValue = selectedVerificationClassFilter;
      }

      const res = await getPaymentVerifications({
        status: verificationStatusFilter || undefined,
        academic_session: verificationSessionFilter || undefined,
        term: verificationTermFilter || undefined,
        q: qValue,
        page: verificationPage,
        per_page: ITEMS_PER_PAGE,
      });

      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];

      // Safeguard client-side filtering if server does not filter by student_id or class_id
      let finalItems = rawArray;
      if (selectedVerificationStudentFilter) {
        finalItems = finalItems.filter(
          (v: PaymentVerificationItem) =>
            v.student?.id === selectedVerificationStudentFilter ||
            (v.student &&
              selectedVerificationStudentFilterObj?.name &&
              [v.student.firstname, v.student.othername, v.student.surname]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(
                  selectedVerificationStudentFilterObj.name.toLowerCase(),
                )),
        );
      }
      if (selectedVerificationClassFilter) {
        finalItems = finalItems.filter((v: any) => {
          const sClassId = v.student?.classroom_id || v.student?.classroom?.id;
          const invClassId =
            v.invoice?.classroom_id || v.invoice?.classroom?.id;
          const directClassId = v.classroom_id || v.class_id;
          if (sClassId) return sClassId === selectedVerificationClassFilter;
          if (invClassId) return invClassId === selectedVerificationClassFilter;
          if (directClassId)
            return directClassId === selectedVerificationClassFilter;
          return true;
        });
      }

      const meta = res?.data?.meta || res?.meta || res?.data;

      setVerifications(finalItems);

      if (
        meta &&
        typeof meta === "object" &&
        (meta.total !== undefined || meta.last_page !== undefined)
      ) {
        const totalItems =
          meta.total !== undefined ? meta.total : rawArray.length;
        const totalPages =
          meta.last_page !== undefined
            ? meta.last_page
            : Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from =
          meta.from !== undefined
            ? meta.from
            : totalItems
              ? (verificationPage - 1) * ITEMS_PER_PAGE + 1
              : 0;
        const to =
          meta.to !== undefined
            ? meta.to
            : totalItems
              ? Math.min(verificationPage * ITEMS_PER_PAGE, totalItems)
              : 0;

        setVerificationPaginationParams({
          page: meta.current_page || verificationPage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setVerificationTotalPages(totalPages);
      } else {
        const totalItems = rawArray.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
        const from = totalItems
          ? (verificationPage - 1) * ITEMS_PER_PAGE + 1
          : 0;
        const to = totalItems
          ? Math.min(verificationPage * ITEMS_PER_PAGE, totalItems)
          : 0;

        setVerificationPaginationParams({
          page: verificationPage,
          totalPages,
          from,
          to,
          totalItems,
        });
        setVerificationTotalPages(totalPages);
      }
    } catch (error) {
      console.error("Error fetching payment verifications:", error);
      setVerifications([]);
    } finally {
      setLoadingVerifications(false);
    }
  };

  const processTableRejection = async () => {
    if (!selectedVerification) return;
    if (!tableRejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setIsRejecting(true);
    try {
      await rejectPaymentVerification({
        id: selectedVerification.id,
        payload: {
          reason: tableRejectionReason.trim(),
        },
      });
      setIsVerificationRejectModalOpen(false);
      setTableRejectionReason("");
      setSelectedVerification(null);
      fetchVerifications();
      fetchInvoices();
      toast.success("Payment rejected successfully.");
    } catch (error) {
      console.error("Failed to reject payment verification:", error);
      toast.error("Failed to reject payment.");
    } finally {
      setIsRejecting(false);
    }
  };

  // Load unpaginated select data once
  useEffect(() => {
    fetchStudentsForSelect();
    fetchClassroomsForSelect();
    fetchAllStationeriesForSelect();
  }, []);

  // Fetch invoices for selected student in on-behalf modal
  useEffect(() => {
    if (!onBehalfSelectedStudentId) {
      setOnBehalfStudentInvoices([]);
      setOnBehalfForm((f) => ({ ...f, invoice_id: "", parent_id: "" }));
      return;
    }
    const fetchForStudent = async () => {
      setLoadingOnBehalfInvoices(true);
      try {
        const res = await getStudentInvoices({
          student_id: onBehalfSelectedStudentId,
        });
        const arr: InvoiceItem[] = Array.isArray(res)
          ? res
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setOnBehalfStudentInvoices(arr);
        setOnBehalfForm((f) => ({ ...f, invoice_id: "", parent_id: "" }));
      } catch {
        setOnBehalfStudentInvoices([]);
      } finally {
        setLoadingOnBehalfInvoices(false);
      }
    };
    fetchForStudent();
  }, [onBehalfSelectedStudentId]);

  // Invoice table server fetch on filter/page change
  useEffect(() => {
    fetchInvoices();
  }, [
    invoiceSessionFilter,
    invoiceTermFilter,
    selectedClassFilter,
    selectedStudentFilter,
    debouncedSearchTerm,
    invoicePage,
  ]);

  // Reset invoice page when search or filters change
  useEffect(() => {
    setInvoicePage(1);
  }, [
    invoiceSessionFilter,
    invoiceTermFilter,
    selectedClassFilter,
    selectedStudentFilter,
    debouncedSearchTerm,
  ]);

  // Stationeries table server fetch on search/page/tab change
  useEffect(() => {
    if (activeTab === "stationeries") {
      fetchStationeriesTable();
    }
  }, [activeTab, debouncedStationerySearchTerm, stationeryPage]);

  // Reset stationery page on search change
  useEffect(() => {
    setStationeryPage(1);
  }, [debouncedStationerySearchTerm]);

  // Verifications table server fetch on status/session/term/class/student/search/page/tab change
  useEffect(() => {
    if (activeTab === "verifications") {
      fetchVerifications();
    }
  }, [
    activeTab,
    verificationStatusFilter,
    verificationSessionFilter,
    verificationTermFilter,
    selectedVerificationClassFilter,
    selectedVerificationStudentFilter,
    debouncedVerificationSearchTerm,
    verificationPage,
  ]);

  // Reset verification page on filter change
  useEffect(() => {
    setVerificationPage(1);
  }, [
    verificationStatusFilter,
    verificationSessionFilter,
    verificationTermFilter,
    selectedVerificationClassFilter,
    selectedVerificationStudentFilter,
    debouncedVerificationSearchTerm,
  ]);

  const handleDeleteStationery = (id: string, name: string) => {
    Swal.fire({
      title: "Delete Stationery?",
      text: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteStationery(id);
          toast.success(`Stationery "${name}" deleted successfully.`);
          fetchStationeriesTable();
          fetchAllStationeriesForSelect();
        } catch (error: any) {
          console.error("Failed to delete stationery:", error);
          toast.error(
            error?.response?.data?.message || "Failed to delete stationery.",
          );
        }
      }
    });
  };

  const handleDeleteInvoice = (invoice: InvoiceItem) => {
    if (Number(invoice.amount_paid || 0) > 0) {
      toast.error(
        "Cannot delete invoice because payments have already been received.",
      );
      return;
    }

    Swal.fire({
      title: "Delete Invoice?",
      text: `Are you sure you want to delete invoice "${invoice.invoice_code || invoice.id}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteInvoice(invoice.id);
          toast.success("Invoice deleted successfully.");
          fetchInvoices();
          if (viewInvoice?.id === invoice.id) {
            setViewInvoice(null);
          }
        } catch (error: any) {
          console.error("Failed to delete invoice:", error);
          toast.error(
            error?.response?.data?.message || "Failed to delete invoice.",
          );
        }
      }
    });
  };

  const handleCreateInvoiceForStationery = (stationeryId: string) => {
    setEditingInvoice(null);
    setPreselectedStationeryId(stationeryId);
    setIsModalOpen(true);
  };

  const handleSetPin = async () => {
    if (
      !setPinForm.current_password ||
      !setPinForm.transaction_pin ||
      !setPinForm.transaction_pin_confirmation
    ) {
      toast.error("Please fill all fields.");
      return;
    }
    if (
      setPinForm.transaction_pin !== setPinForm.transaction_pin_confirmation
    ) {
      toast.error("PINs do not match.");
      return;
    }
    if (!/^\d{6}$/.test(setPinForm.transaction_pin)) {
      toast.error("PIN must be exactly 6 digits.");
      return;
    }
    setIsSettingPin(true);
    try {
      await setTransactionPin(setPinForm);
      toast.success("Transaction PIN set successfully.");
      setIsSetPinModalOpen(false);
      setSetPinForm({
        current_password: "",
        transaction_pin: "",
        transaction_pin_confirmation: "",
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to set PIN.");
    } finally {
      setIsSettingPin(false);
    }
  };

  const resetOnBehalfModal = () => {
    setOnBehalfSelectedStudentId("");
    setOnBehalfSelectedStudentObj(null);
    setOnBehalfStudentSearchQuery("");
    setOnBehalfStudentList([]);
    setOnBehalfStudentInvoices([]);
    setOnBehalfProofFile(null);
    setOnBehalfProofPreview("");
    setOnBehalfForm({
      invoice_id: "",
      parent_id: "",
      amount_paid: "",
      payment_method: "transfer",
      admin_notes: "",
      transaction_pin: "",
    });
  };

  const handlePostOnBehalf = async () => {
    if (
      !onBehalfForm.invoice_id ||
      !onBehalfForm.parent_id ||
      !onBehalfForm.amount_paid ||
      !onBehalfForm.transaction_pin
    ) {
      toast.error("Please fill all required fields.");
      return;
    }
    setIsPostingOnBehalf(true);
    try {
      let proofUrl: string | undefined;
      if (onBehalfProofFile) {
        setUploadingProof(true);
        proofUrl = await uploadImageToCloudinary(onBehalfProofFile);
        setUploadingProof(false);
      }
      await postPaymentOnBehalf({
        invoice_id: onBehalfForm.invoice_id.trim(),
        parent_id: onBehalfForm.parent_id.trim(),
        amount_paid: Number(onBehalfForm.amount_paid),
        payment_method: onBehalfForm.payment_method,
        proof_url: proofUrl || undefined,
        admin_notes: onBehalfForm.admin_notes.trim() || undefined,
        transaction_pin: onBehalfForm.transaction_pin,
      });
      toast.success("Payment posted on behalf of parent successfully.");
      setIsOnBehalfModalOpen(false);
      resetOnBehalfModal();
      fetchInvoices();
      if (activeTab === "verifications") fetchVerifications();
    } catch (error: any) {
      setUploadingProof(false);
      toast.error(
        error?.response?.data?.message || "Failed to post payment on behalf.",
      );
    } finally {
      setIsPostingOnBehalf(false);
    }
  };

  // Reference backend summary object for accurate KPI analytics across all pages
  const totalIssuedAmount = Number(
    invoiceSummary?.total_invoiced_amount ??
      invoiceSummary?.total_invoiced ??
      invoiceSummary?.total_issued_amount ??
      invoiceSummary?.total_amount ??
      invoiceSummary?.totalIssuedAmount ??
      invoiceSummary?.total_invoice_amount ??
      (invoiceSummary
        ? 0
        : invoices.reduce(
            (sum, inv) => sum + Number(inv.total_amount || 0),
            0,
          )),
  );

  const totalPaidAmount = Number(
    invoiceSummary?.total_amount_paid ??
      invoiceSummary?.total_paid_amount ??
      invoiceSummary?.total_paid ??
      invoiceSummary?.amount_paid ??
      invoiceSummary?.totalPaidAmount ??
      invoiceSummary?.paid_amount ??
      (invoiceSummary
        ? 0
        : invoices.reduce((sum, inv) => sum + Number(inv.amount_paid || 0), 0)),
  );

  const totalRemainingBalance = Number(
    invoiceSummary?.total_remaining_balance ??
      invoiceSummary?.remaining_balance ??
      invoiceSummary?.total_balance ??
      invoiceSummary?.total_outstanding_balance ??
      invoiceSummary?.outstanding_balance ??
      invoiceSummary?.totalRemainingBalance ??
      invoiceSummary?.balance_due ??
      invoiceSummary?.total_balance_due ??
      (invoiceSummary
        ? totalIssuedAmount - totalPaidAmount
        : invoices.reduce(
            (sum, inv) =>
              sum + Number(inv.remaining_balance ?? (inv.total_amount || 0)),
            0,
          )),
  );

  const getStatusBadge = (status?: string, remaining?: number) => {
    if (status === "paid" || remaining === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700">
          <CheckCircle2 size={13} /> Paid
        </span>
      );
    }
    if (status === "partially_paid") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-100 text-amber-700">
          <Clock size={13} /> Partially Paid
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-blue-100 text-blue-700">
        <FileText size={13} /> Issued
      </span>
    );
  };

  const sessions = generateAcademicSessions(2025);

  const activeVerificationFiltersCount =
    (verificationStatusFilter ? 1 : 0) +
    (selectedVerificationClassFilter ? 1 : 0) +
    (selectedVerificationStudentFilter ? 1 : 0) +
    (verificationSessionFilter && verificationSessionFilter !== "2026/2027"
      ? 1
      : 0) +
    (verificationTermFilter ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TopAdminNav title="Stationeries & Student Invoices" name="Admin" />

      <div className="max-w-[1400px] mx-auto p-4 md:p-8 space-y-8 animate-in fade-in duration-500">
        {/* Header Title & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <div>
            <h1 className="text-3xl font-black text-slate-800 leading-tight">
              Stationeries & Invoice Management
            </h1>
            <p className="text-slate-400 font-bold text-xs mt-1">
              Manage school stationeries catalog, issue student invoices, and
              verify payments.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {
              <button
                type="button"
                onClick={() => setIsOnBehalfModalOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-100 flex items-center gap-2"
              >
                <CreditCard size={18} />
                Post Payment
              </button>
            }

            <button
              type="button"
              onClick={() => {
                setEditingStationery(null);
                setIsStationeryModalOpen(true);
              }}
              className="bg-slate-100 hover:bg-slate-200 text-slate-800 px-6 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2"
            >
              <BookOpen size={18} />+ Add Stationery
            </button>

            <button
              type="button"
              onClick={() => {
                setEditingInvoice(null);
                setPreselectedStationeryId(null);
                setIsModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-100 flex items-center gap-2"
            >
              <Plus size={18} />
              Issue Invoice
            </button>
          </div>
        </div>

        {/* Top Navigation Tabs */}
        <div className="flex bg-white p-2 rounded-[2rem] border border-slate-100 w-full sm:w-max shadow-sm gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("invoices")}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all ${
              activeTab === "invoices"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <FileText size={16} />
            Student Invoices (
            {invoicePaginationParams.totalItems || invoices.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("stationeries")}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all ${
              activeTab === "stationeries"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <BookOpen size={16} />
            Stationery Catalog (
            {stationeryPaginationParams.totalItems || stationeriesTable.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("verifications")}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all ${
              activeTab === "verifications"
                ? "bg-slate-900 text-white shadow-md"
                : "text-slate-500 hover:bg-slate-50"
            }`}
          >
            <ShieldCheck size={16} />
            Payment Verifications (
            {verificationPaginationParams.totalItems || verifications.length})
          </button>
        </div>

        {/* TAB 1: INVOICES */}
        {activeTab === "invoices" && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* KPI Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-900 text-white p-6 rounded-[2rem] shadow-xl border border-slate-800 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Total Invoiced Amount
                  </span>
                  <h3 className="text-2xl font-black text-white">
                    ₦{totalIssuedAmount.toLocaleString()}
                  </h3>
                  <p className="text-xs text-slate-400 font-medium mt-1">
                    {invoiceSummary?.total_invoices ??
                      invoiceSummary?.total_count ??
                      invoiceSummary?.total ??
                      (invoicePaginationParams.totalItems ||
                        invoices.length)}{" "}
                    total invoice(s)
                  </p>
                </div>
                <div className="w-14 h-14 bg-blue-600/20 text-blue-400 rounded-2xl flex items-center justify-center border border-blue-500/30">
                  <FileText size={26} />
                </div>
              </div>

              <div className="bg-emerald-950 text-white p-6 rounded-[2rem] shadow-xl border border-emerald-900 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-1">
                    Total Amount Paid
                  </span>
                  <h3 className="text-2xl font-black text-emerald-400">
                    ₦{totalPaidAmount.toLocaleString()}
                  </h3>
                  <p className="text-xs text-emerald-300/70 font-medium mt-1">
                    Received settlements
                  </p>
                </div>
                <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                  <CheckCircle2 size={26} />
                </div>
              </div>

              <div className="bg-rose-950 text-white p-6 rounded-[2rem] shadow-xl border border-rose-900 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-black text-rose-300 uppercase tracking-widest block mb-1">
                    Outstanding Balance Due
                  </span>
                  <h3 className="text-2xl font-black text-rose-400">
                    ₦{totalRemainingBalance.toLocaleString()}
                  </h3>
                  <p className="text-xs text-rose-300/70 font-medium mt-1">
                    Pending collection
                  </p>
                </div>
                <div className="w-14 h-14 bg-rose-500/20 text-rose-400 rounded-2xl flex items-center justify-center border border-rose-500/30">
                  <Clock size={26} />
                </div>
              </div>
            </div>

            {/* Filter Controls & Search */}
            <div className="bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm flex flex-col xl:flex-row items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5">
                  <Filter size={16} className="text-slate-400" />
                  <span className="text-xs font-black text-slate-400 uppercase tracking-wider">
                    Session:
                  </span>
                  <select
                    value={invoiceSessionFilter}
                    onChange={(e) => setInvoiceSessionFilter(e.target.value)}
                    className="bg-transparent font-bold text-xs text-slate-800 outline-none"
                  >
                    <option value="">All Sessions</option>
                    {sessions.map(({ value: s }) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5">
                  <span className="text-xs font-black text-slate-400 uppercase tracking-wider">
                    Term:
                  </span>
                  <select
                    value={invoiceTermFilter}
                    onChange={(e) => setInvoiceTermFilter(e.target.value)}
                    className="bg-transparent font-bold text-xs text-slate-800 outline-none"
                  >
                    <option value="">All Terms</option>
                    <option value="1">Term 1</option>
                    <option value="2">Term 2</option>
                    <option value="3">Term 3</option>
                  </select>
                </div>

                {/* Class Select Filter */}
                <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5">
                  <BookOpen size={16} className="text-slate-400 shrink-0" />
                  <span className="text-xs font-black text-slate-400 uppercase tracking-wider">
                    Class:
                  </span>
                  <select
                    value={selectedClassFilter}
                    onChange={(e) => setSelectedClassFilter(e.target.value)}
                    className="bg-transparent font-bold text-xs text-slate-800 outline-none max-w-[130px] truncate"
                  >
                    <option value="">All Classes</option>
                    {classrooms.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {selectedClassFilter && (
                    <button
                      type="button"
                      onClick={() => setSelectedClassFilter("")}
                      className="text-slate-400 hover:text-slate-600 shrink-0 ml-1 p-0.5"
                      title="Clear class filter"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                {/* Searchable Student Select Filter with Server-side Query */}
                <div ref={studentFilterDropdownRef} className="relative">
                  <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5 max-w-full sm:max-w-xs">
                    <User size={16} className="text-slate-400 shrink-0" />
                    <button
                      type="button"
                      onClick={() =>
                        setIsStudentFilterDropdownOpen(
                          !isStudentFilterDropdownOpen,
                        )
                      }
                      className="bg-transparent font-bold text-xs text-slate-800 outline-none flex items-center justify-between gap-2 max-w-[170px] truncate text-left"
                    >
                      <span className="truncate">
                        {selectedStudentFilterObj
                          ? selectedStudentFilterObj.name
                          : "All Students"}
                      </span>
                      <ChevronDown
                        size={14}
                        className="text-slate-400 shrink-0"
                      />
                    </button>
                    {selectedStudentFilter && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStudentFilter("");
                          setSelectedStudentFilterObj(null);
                        }}
                        className="text-slate-400 hover:text-slate-600 shrink-0 ml-1 p-0.5"
                        title="Clear student filter"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  {/* Dropdown Menu */}
                  {isStudentFilterDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-2xl z-40 p-3 space-y-2 max-h-64 flex flex-col animate-in fade-in zoom-in-95 duration-150">
                      <div className="relative flex items-center shrink-0">
                        <Search
                          size={15}
                          className="absolute left-3 text-slate-400"
                        />
                        <input
                          type="text"
                          autoFocus
                          placeholder="Search student (server-side)..."
                          value={studentFilterSearchQuery}
                          onChange={(e) =>
                            setStudentFilterSearchQuery(e.target.value)
                          }
                          className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl pl-9 pr-7 py-2 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 placeholder:text-slate-400"
                        />
                        {fetchingFilterStudents && (
                          <Loader2
                            size={13}
                            className="absolute right-2.5 text-blue-600 animate-spin"
                          />
                        )}
                      </div>

                      <div className="overflow-y-auto flex-1 divide-y divide-slate-50 pr-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedStudentFilter("");
                            setSelectedStudentFilterObj(null);
                            setIsStudentFilterDropdownOpen(false);
                          }}
                          className={`w-full text-left p-2.5 rounded-xl font-bold text-xs flex items-center justify-between transition-colors ${
                            !selectedStudentFilter
                              ? "bg-blue-50 text-blue-700"
                              : "hover:bg-slate-50 text-slate-600"
                          }`}
                        >
                          <span>-- All Students --</span>
                          {!selectedStudentFilter && (
                            <Check size={14} className="text-blue-600" />
                          )}
                        </button>

                        {studentFilterList.length === 0 ? (
                          <div className="p-3 text-center text-xs text-slate-400 font-medium">
                            {fetchingFilterStudents
                              ? "Searching..."
                              : `No students found matching "${studentFilterSearchQuery}"`}
                          </div>
                        ) : (
                          studentFilterList.map((st: any) => {
                            const isSelected = selectedStudentFilter === st.id;
                            return (
                              <button
                                key={st.id}
                                type="button"
                                onClick={() => {
                                  setSelectedStudentFilter(st.id);
                                  setSelectedStudentFilterObj(st);
                                  setIsStudentFilterDropdownOpen(false);
                                }}
                                className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-colors ${
                                  isSelected
                                    ? "bg-blue-50 text-blue-700 font-bold"
                                    : "hover:bg-slate-50 text-slate-700"
                                }`}
                              >
                                <div className="flex items-center gap-1.5 overflow-hidden">
                                  <span className="font-bold text-xs truncate">
                                    {st.name}
                                  </span>
                                  {st.classroom?.name && (
                                    <span className="bg-slate-100 text-slate-600 text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0">
                                      {st.classroom.name}
                                    </span>
                                  )}
                                </div>
                                {isSelected && (
                                  <Check
                                    size={14}
                                    className="text-blue-600 shrink-0"
                                  />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full xl:w-80">
                <div className="relative flex items-center bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5 focus-within:border-blue-500 focus-within:bg-white transition-all">
                  <Search size={18} className="text-slate-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Search by invoice code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-transparent outline-none text-xs font-bold text-slate-700 ml-2 pr-6 placeholder:text-slate-400"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 p-1 text-slate-400 hover:text-slate-600 rounded-lg"
                      title="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Table */}
            <div className="bg-white rounded-[2.5rem] p-6 md:p-8 border border-slate-100 shadow-sm">
              {loadingInvoices ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-slate-400 font-bold text-sm">
                    Loading student invoices...
                  </p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="bg-[#F8FAFC] rounded-[2rem] p-12 text-center border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                  <FileText size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-xl font-black text-slate-800 mb-1">
                    No Invoices Found
                  </h3>
                  <p className="text-slate-400 font-medium text-xs max-w-sm mb-6">
                    No invoices match the selected criteria.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingInvoice(null);
                      setPreselectedStationeryId(null);
                      setIsModalOpen(true);
                    }}
                    className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-3.5 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Issue First Invoice
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 text-[11px] font-black text-slate-400 uppercase tracking-widest">
                        <th className="pb-4 px-4">Invoice Code</th>
                        <th className="pb-4 px-4">Student</th>
                        <th className="pb-4 px-4">Session & Term</th>
                        <th className="pb-4 px-4">Total Amount</th>
                        <th className="pb-4 px-4">Amount Paid</th>
                        <th className="pb-4 px-4">Remaining</th>
                        <th className="pb-4 px-4">Status</th>
                        <th className="pb-4 px-4">Due Date</th>
                        <th className="pb-4 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {invoices.map((inv) => (
                        <tr
                          key={inv.id}
                          className="hover:bg-slate-50/60 transition-colors group"
                        >
                          <td className="py-4 px-4">
                            <span className="inline-block font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2.5 py-1 rounded-xl border border-slate-200">
                              {inv.invoice_code || "—"}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="font-black text-slate-800 text-sm">
                              {inv.student?.name ||
                                "Student #" + inv.student_id?.substring(0, 6)}
                            </div>
                            {inv.student?.email && (
                              <div className="text-xs text-slate-400 font-medium">
                                {inv.student.email}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <span className="inline-block bg-slate-100 text-slate-700 font-bold text-xs px-3 py-1 rounded-xl">
                              {inv.academic_session} • Term {inv.term}
                            </span>
                          </td>
                          <td className="py-4 px-4 font-black text-slate-900 text-sm">
                            ₦{Number(inv.total_amount || 0).toLocaleString()}
                          </td>
                          <td className="py-4 px-4 font-bold text-emerald-600 text-sm">
                            ₦{Number(inv.amount_paid || 0).toLocaleString()}
                          </td>
                          <td className="py-4 px-4 font-bold text-rose-600 text-sm">
                            ₦
                            {Number(
                              inv.remaining_balance ?? inv.total_amount,
                            ).toLocaleString()}
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(inv.status, inv.remaining_balance)}
                          </td>
                          <td className="py-4 px-4 text-xs font-bold text-slate-500">
                            {inv.due_date || "—"}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setViewInvoice(inv)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                                title="View Details"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingInvoice(inv);
                                  setIsModalOpen(true);
                                }}
                                className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
                                title="Edit Invoice"
                              >
                                <Edit3 size={16} />
                              </button>
                              {(() => {
                                const isPaidOrPartial =
                                  Number(inv.amount_paid || 0) > 0;
                                return (
                                  <button
                                    type="button"
                                    disabled={isPaidOrPartial}
                                    onClick={() => handleDeleteInvoice(inv)}
                                    className={`p-2 rounded-xl transition-all ${
                                      isPaidOrPartial
                                        ? "text-slate-300 cursor-not-allowed opacity-30 hover:bg-transparent"
                                        : "text-rose-600 hover:bg-rose-50 hover:text-rose-700 cursor-pointer"
                                    }`}
                                    title={
                                      isPaidOrPartial
                                        ? `Cannot delete invoice: ₦${Number(inv.amount_paid).toLocaleString()} already paid`
                                        : "Delete Invoice"
                                    }
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Server-side Pagination Footer */}
                  <Pagination
                    totalPages={invoiceTotalPages}
                    page={invoicePage}
                    setPage={setInvoicePage}
                    paginationParams={invoicePaginationParams}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: STATIONERY CATALOG */}
        {activeTab === "stationeries" && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Search and Header actions */}
            <div className="bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="w-full md:w-96">
                <div className="relative flex items-center bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-3">
                  <Search size={18} className="text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search stationeries by name or description..."
                    value={stationerySearchTerm}
                    onChange={(e) => setStationerySearchTerm(e.target.value)}
                    className="w-full bg-transparent outline-none text-xs font-bold text-slate-700 ml-2 placeholder:text-slate-300"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setEditingStationery(null);
                  setIsStationeryModalOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all shadow-md shadow-blue-100 flex items-center gap-2"
              >
                <Plus size={16} />
                Add New Stationery
              </button>
            </div>

            {/* Stationeries Grid/Table */}
            <div className="bg-white rounded-[2.5rem] p-6 md:p-8 border border-slate-100 shadow-sm">
              {loadingStationeries ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-slate-400 font-bold text-sm">
                    Loading stationeries list...
                  </p>
                </div>
              ) : stationeriesTable.length === 0 ? (
                <div className="bg-[#F8FAFC] rounded-[2rem] p-12 text-center border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                  <BookOpen size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-xl font-black text-slate-800 mb-1">
                    No Stationeries Found
                  </h3>
                  <p className="text-slate-400 font-medium text-xs max-w-sm mb-6">
                    {stationerySearchTerm
                      ? `No stationery items match "${stationerySearchTerm}".`
                      : "No stationery items available in the catalog yet."}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingStationery(null);
                      setIsStationeryModalOpen(true);
                    }}
                    className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-3.5 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Create First Stationery
                  </button>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {stationeriesTable.map((st) => (
                      <div
                        key={st.id}
                        className="bg-[#F8FAFC] p-6 rounded-[2rem] border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-50 transition-all flex flex-col justify-between group"
                      >
                        <div>
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="w-12 h-12 bg-white text-blue-600 rounded-2xl flex items-center justify-center border border-slate-200 shadow-sm shrink-0">
                              <BookOpen size={22} />
                            </div>
                            <span
                              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                st.is_active !== false
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-200 text-slate-600"
                              }`}
                            >
                              {st.is_active !== false ? "Active" : "Inactive"}
                            </span>
                          </div>

                          <h3 className="text-lg font-black text-slate-800 group-hover:text-blue-600 transition-colors">
                            {st.name}
                          </h3>
                          <p className="text-xs text-slate-500 font-medium mt-1 line-clamp-2 min-h-[2rem]">
                            {st.description || "No description provided."}
                          </p>

                          <div className="mt-4 pt-4 border-t border-slate-200/60 flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              Unit Price
                            </span>
                            <span className="text-xl font-black text-slate-900">
                              ₦{(st.unit_price || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-200/60 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleCreateInvoiceForStationery(st.id)
                            }
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-sm"
                            title="Issue Invoice for this stationery"
                          >
                            <ShoppingBag size={14} /> Issue Invoice
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setEditingStationery(st);
                              setIsStationeryModalOpen(true);
                            }}
                            className="p-2.5 text-slate-600 hover:bg-white hover:text-blue-600 border border-slate-200 rounded-xl transition-all"
                            title="Edit Stationery"
                          >
                            <Edit3 size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteStationery(st.id, st.name)
                            }
                            className="p-2.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 border border-slate-200 rounded-xl transition-all"
                            title="Delete Stationery"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Server-side Pagination Footer */}
                  <Pagination
                    totalPages={stationeryTotalPages}
                    page={stationeryPage}
                    setPage={setStationeryPage}
                    paginationParams={stationeryPaginationParams}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: PAYMENT VERIFICATIONS */}
        {activeTab === "verifications" && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Filter Toolbar with Filter Button */}
            <div className="bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                {/* Search Box */}
                <div className="relative flex-1 max-w-md">
                  <div className="relative flex items-center bg-[#F8FAFC] border border-slate-200 rounded-2xl px-4 py-2.5">
                    <Search size={18} className="text-slate-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Search code, student, proof..."
                      value={verificationSearchTerm}
                      onChange={(e) =>
                        setVerificationSearchTerm(e.target.value)
                      }
                      className="w-full bg-transparent outline-none text-xs font-bold text-slate-700 ml-2 placeholder:text-slate-400"
                    />
                    {verificationSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setVerificationSearchTerm("")}
                        className="text-slate-400 hover:text-slate-600 p-0.5"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Trigger Button & Quick Clear */}
                <div className="flex items-center gap-3 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={() =>
                      setIsVerificationFilterPanelOpen(
                        !isVerificationFilterPanelOpen,
                      )
                    }
                    className={`px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2.5 border ${
                      isVerificationFilterPanelOpen ||
                      activeVerificationFiltersCount > 0
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200/50"
                        : "bg-[#F8FAFC] text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <Filter size={15} />
                    <span>Filters</span>
                    {activeVerificationFiltersCount > 0 && (
                      <span className="w-5 h-5 rounded-full bg-white text-emerald-700 text-[11px] font-black flex items-center justify-center shrink-0">
                        {activeVerificationFiltersCount}
                      </span>
                    )}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        isVerificationFilterPanelOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {activeVerificationFiltersCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setVerificationStatusFilter("");
                        setSelectedVerificationClassFilter("");
                        setSelectedVerificationStudentFilter("");
                        setSelectedVerificationStudentFilterObj(null);
                        setVerificationTermFilter("");
                        setVerificationSessionFilter("2026/2027");
                      }}
                      className="px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center gap-1.5"
                      title="Clear all active filters"
                    >
                      <X size={13} /> Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Collapsible Filter Options Panel */}
              {isVerificationFilterPanelOpen && (
                <div className="bg-[#F8FAFC] rounded-3xl p-5 border border-slate-200/80 shadow-inner space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between border-b border-slate-200/60 pb-3">
                    <div className="flex items-center gap-2">
                      <Filter size={15} className="text-emerald-600" />
                      <span className="font-black text-slate-800 text-xs uppercase tracking-wider">
                        Filter By Parameters
                      </span>
                      {activeVerificationFiltersCount > 0 && (
                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-md">
                          {activeVerificationFiltersCount} active
                        </span>
                      )}
                    </div>

                    {activeVerificationFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setVerificationStatusFilter("");
                          setSelectedVerificationClassFilter("");
                          setSelectedVerificationStudentFilter("");
                          setSelectedVerificationStudentFilterObj(null);
                          setVerificationTermFilter("");
                          setVerificationSessionFilter("2026/2027");
                        }}
                        className="text-xs font-bold text-rose-600 hover:text-rose-700 hover:underline flex items-center gap-1"
                      >
                        <X size={13} /> Reset All Filters
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* 1. Status Filter */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">
                        Payment Status
                      </label>
                      <select
                        value={verificationStatusFilter}
                        onChange={(e) =>
                          setVerificationStatusFilter(e.target.value)
                        }
                        className="w-full bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 shadow-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    {/* 2. Class Filter */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">
                        Classroom
                      </label>
                      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
                        <BookOpen
                          size={15}
                          className="text-slate-400 shrink-0"
                        />
                        <select
                          value={selectedVerificationClassFilter}
                          onChange={(e) =>
                            setSelectedVerificationClassFilter(e.target.value)
                          }
                          className="w-full bg-transparent font-bold text-xs text-slate-800 outline-none"
                        >
                          <option value="">All Classes</option>
                          {classrooms.map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {selectedVerificationClassFilter && (
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedVerificationClassFilter("")
                            }
                            className="text-slate-400 hover:text-slate-600 shrink-0 ml-1 p-0.5"
                            title="Clear class filter"
                          >
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* 3. Student Filter */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">
                        Student
                      </label>
                      <div
                        ref={verificationStudentFilterDropdownRef}
                        className="relative"
                      >
                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
                          <div className="flex items-center gap-2 overflow-hidden w-full">
                            <User
                              size={15}
                              className="text-slate-400 shrink-0"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setIsVerificationStudentFilterDropdownOpen(
                                  !isVerificationStudentFilterDropdownOpen,
                                )
                              }
                              className="bg-transparent font-bold text-xs text-slate-800 outline-none flex items-center justify-between gap-2 w-full truncate text-left"
                            >
                              <span className="truncate">
                                {selectedVerificationStudentFilterObj
                                  ? selectedVerificationStudentFilterObj.name
                                  : "All Students"}
                              </span>
                              <ChevronDown
                                size={14}
                                className="text-slate-400 shrink-0"
                              />
                            </button>
                          </div>
                          {selectedVerificationStudentFilter && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedVerificationStudentFilter("");
                                setSelectedVerificationStudentFilterObj(null);
                              }}
                              className="text-slate-400 hover:text-slate-600 shrink-0 ml-1 p-0.5"
                              title="Clear student filter"
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>

                        {/* Dropdown Menu */}
                        {isVerificationStudentFilterDropdownOpen && (
                          <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-2xl z-40 p-3 space-y-2 max-h-64 flex flex-col animate-in fade-in zoom-in-95 duration-150">
                            <div className="relative flex items-center shrink-0">
                              <Search
                                size={15}
                                className="absolute left-3 text-slate-400"
                              />
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search student..."
                                value={verificationStudentFilterSearchQuery}
                                onChange={(e) =>
                                  setVerificationStudentFilterSearchQuery(
                                    e.target.value,
                                  )
                                }
                                className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl pl-9 pr-7 py-2 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 placeholder:text-slate-400"
                              />
                              {fetchingVerificationFilterStudents && (
                                <Loader2
                                  size={13}
                                  className="absolute right-2.5 text-emerald-600 animate-spin"
                                />
                              )}
                            </div>

                            <div className="overflow-y-auto flex-1 divide-y divide-slate-50 pr-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedVerificationStudentFilter("");
                                  setSelectedVerificationStudentFilterObj(null);
                                  setIsVerificationStudentFilterDropdownOpen(
                                    false,
                                  );
                                }}
                                className={`w-full text-left p-2.5 rounded-xl font-bold text-xs flex items-center justify-between transition-colors ${
                                  !selectedVerificationStudentFilter
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "hover:bg-slate-50 text-slate-600"
                                }`}
                              >
                                <span>-- All Students --</span>
                                {!selectedVerificationStudentFilter && (
                                  <Check
                                    size={14}
                                    className="text-emerald-600"
                                  />
                                )}
                              </button>

                              {verificationStudentFilterList.length === 0 ? (
                                <div className="p-3 text-center text-xs text-slate-400 font-medium">
                                  {fetchingVerificationFilterStudents
                                    ? "Searching..."
                                    : `No students found matching "${verificationStudentFilterSearchQuery}"`}
                                </div>
                              ) : (
                                verificationStudentFilterList.map((st: any) => {
                                  const isSelected =
                                    selectedVerificationStudentFilter === st.id;
                                  return (
                                    <button
                                      key={st.id}
                                      type="button"
                                      onClick={() => {
                                        setSelectedVerificationStudentFilter(
                                          st.id,
                                        );
                                        setSelectedVerificationStudentFilterObj(
                                          st,
                                        );
                                        setIsVerificationStudentFilterDropdownOpen(
                                          false,
                                        );
                                      }}
                                      className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-colors ${
                                        isSelected
                                          ? "bg-emerald-50 text-emerald-700 font-bold"
                                          : "hover:bg-slate-50 text-slate-700"
                                      }`}
                                    >
                                      <div className="flex items-center gap-1.5 overflow-hidden">
                                        <span className="font-bold text-xs truncate">
                                          {st.name}
                                        </span>
                                        {st.classroom?.name && (
                                          <span className="bg-slate-100 text-slate-600 text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0">
                                            {st.classroom.name}
                                          </span>
                                        )}
                                      </div>
                                      {isSelected && (
                                        <Check
                                          size={14}
                                          className="text-emerald-600 shrink-0"
                                        />
                                      )}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 4. Session & Term Filter */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">
                          Session
                        </label>
                        <select
                          value={verificationSessionFilter}
                          onChange={(e) =>
                            setVerificationSessionFilter(e.target.value)
                          }
                          className="w-full bg-white border border-slate-200 rounded-2xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 shadow-sm"
                        >
                          <option value="">All</option>
                          {sessions.map(({ value: s }) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">
                          Term
                        </label>
                        <select
                          value={verificationTermFilter}
                          onChange={(e) =>
                            setVerificationTermFilter(e.target.value)
                          }
                          className="w-full bg-white border border-slate-200 rounded-2xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 shadow-sm"
                        >
                          <option value="">All</option>
                          <option value="1">Term 1</option>
                          <option value="2">Term 2</option>
                          <option value="3">Term 3</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filter Chips Strip */}
              {activeVerificationFiltersCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Active Filters:
                  </span>
                  {verificationStatusFilter && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                      Status:{" "}
                      <strong className="capitalize">
                        {verificationStatusFilter}
                      </strong>
                      <button
                        type="button"
                        onClick={() => setVerificationStatusFilter("")}
                        className="text-slate-400 hover:text-slate-700 ml-0.5"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {selectedVerificationClassFilter && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      Class:{" "}
                      <strong>
                        {classrooms.find(
                          (c: any) => c.id === selectedVerificationClassFilter,
                        )?.name || "Selected"}
                      </strong>
                      <button
                        type="button"
                        onClick={() => setSelectedVerificationClassFilter("")}
                        className="text-blue-400 hover:text-blue-700 ml-0.5"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {selectedVerificationStudentFilter && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Student:{" "}
                      <strong>
                        {selectedVerificationStudentFilterObj?.name ||
                          "Selected"}
                      </strong>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedVerificationStudentFilter("");
                          setSelectedVerificationStudentFilterObj(null);
                        }}
                        className="text-emerald-400 hover:text-emerald-700 ml-0.5"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {verificationSessionFilter &&
                    verificationSessionFilter !== "2026/2027" && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                        Session: <strong>{verificationSessionFilter}</strong>
                        <button
                          type="button"
                          onClick={() =>
                            setVerificationSessionFilter("2026/2027")
                          }
                          className="text-slate-400 hover:text-slate-700 ml-0.5"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                  {verificationTermFilter && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                      Term: <strong>Term {verificationTermFilter}</strong>
                      <button
                        type="button"
                        onClick={() => setVerificationTermFilter("")}
                        className="text-slate-400 hover:text-slate-700 ml-0.5"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Verifications List / Table */}
            <div className="bg-white rounded-[2.5rem] p-6 md:p-8 border border-slate-100 shadow-sm">
              {loadingVerifications ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
                  <p className="text-slate-400 font-bold text-sm">
                    Loading payment verifications...
                  </p>
                </div>
              ) : verifications.length === 0 ? (
                <div className="bg-[#F8FAFC] rounded-[2rem] p-12 text-center border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                  <ShieldCheck size={48} className="text-slate-300 mb-4" />
                  <h3 className="text-xl font-black text-slate-800 mb-1">
                    No Verifications Found
                  </h3>
                  <p className="text-slate-400 font-medium text-xs max-w-sm">
                    {selectedVerificationStudentFilterObj
                      ? `No payment proof submissions found for ${selectedVerificationStudentFilterObj.name}.`
                      : `No payment proof submissions found for status "${verificationStatusFilter || "all"}".`}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 text-[11px] font-black text-slate-400 uppercase tracking-widest">
                        <th className="pb-4 px-4">Invoice Code</th>
                        <th className="pb-4 px-4">Student</th>
                        <th className="pb-4 px-4">Amount Paid</th>
                        <th className="pb-4 px-4">Verified Amount</th>
                        <th className="pb-4 px-4">Proof Receipt</th>
                        <th className="pb-4 px-4">Status</th>
                        <th className="pb-4 px-4">Submitted Date</th>
                        <th className="pb-4 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {verifications.map((item) => (
                        <tr
                          key={item.id}
                          className="hover:bg-slate-50/60 transition-colors"
                        >
                          <td className="py-4 px-4">
                            <span className="inline-block font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2.5 py-1 rounded-xl border border-slate-200">
                              {item.invoice_code ||
                                item.invoice?.invoice_code ||
                                (item.invoice_id
                                  ? item.invoice_id.substring(0, 8) + "..."
                                  : "—")}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {item.student ? (
                              <div className="flex items-center gap-3">
                                <div className="relative w-9 h-9 shrink-0">
                                  {item.student.profile_picture ? (
                                    <img
                                      src={item.student.profile_picture}
                                      alt={
                                        [
                                          item.student.firstname,
                                          item.student.surname,
                                        ]
                                          .filter(Boolean)
                                          .join(" ") || "Student"
                                      }
                                      className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-sm"
                                      onError={(e) => {
                                        (
                                          e.currentTarget as HTMLElement
                                        ).style.display = "none";
                                        const fallback =
                                          e.currentTarget.parentElement?.querySelector(
                                            ".avatar-fallback",
                                          );
                                        if (fallback)
                                          (
                                            fallback as HTMLElement
                                          ).style.display = "flex";
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`avatar-fallback w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-black text-xs items-center justify-center border border-emerald-200 ${
                                      item.student.profile_picture
                                        ? "hidden"
                                        : "flex"
                                    }`}
                                  >
                                    {item.student.firstname?.[0]?.toUpperCase() ||
                                      item.student.surname?.[0]?.toUpperCase() || (
                                        <User size={15} />
                                      )}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-black text-slate-800 text-sm whitespace-nowrap">
                                    {[
                                      item.student.firstname,
                                      item.student.othername,
                                      item.student.surname,
                                    ]
                                      .filter(Boolean)
                                      .join(" ") || "Student"}
                                  </div>
                                  {item.student.email && (
                                    <div className="text-[11px] text-slate-400 font-medium truncate max-w-[160px]">
                                      {item.student.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : item.invoice?.student ? (
                              <div className="font-black text-slate-800 text-sm">
                                {item.invoice.student.name}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 font-medium">
                                —
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 font-black text-slate-900 text-sm">
                            ₦
                            {Number(item.amount_paid || 0).toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {item.status === "approved" ||
                            (item.verified_amount !== undefined &&
                              item.verified_amount !== null &&
                              item.verified_amount > 0) ? (
                              <span className="font-black text-emerald-600 text-sm">
                                ₦
                                {Number(
                                  item.verified_amount || item.amount_paid || 0,
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 font-bold">
                                —
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {item.proof_url ? (
                              <a
                                href={item.proof_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                              >
                                <ImageIcon size={13} /> View Receipt
                              </a>
                            ) : (
                              <span className="text-xs text-slate-400">
                                No Image
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {item.status === "approved" ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700">
                                <CheckCircle2 size={13} /> Approved
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-100 text-amber-700">
                                <Clock size={13} /> Pending Verification
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-xs font-bold text-slate-500">
                            {item.created_at || "—"}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedVerification(item);
                                  setVerificationDefaultAction(null);
                                  setIsVerificationModalOpen(true);
                                }}
                                className="px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1.5"
                              >
                                <Eye size={13} /> View More
                              </button>
                              {item.status !== "approved" &&
                                item.status !== "rejected" && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedVerification(item);
                                        setVerificationDefaultAction("approve");
                                        setIsVerificationModalOpen(true);
                                      }}
                                      className="px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-100"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedVerification(item);
                                        setTableRejectionReason("");
                                        setIsVerificationRejectModalOpen(true);
                                      }}
                                      className="px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all bg-rose-50 text-rose-600 hover:bg-rose-100"
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Server-side Pagination Footer */}
                  <Pagination
                    totalPages={verificationTotalPages}
                    page={verificationPage}
                    setPage={setVerificationPage}
                    paginationParams={verificationPaginationParams}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stationery Modal for Add/Edit */}
      {isStationeryModalOpen && (
        <StationeryModal
          isOpen={isStationeryModalOpen}
          onClose={() => setIsStationeryModalOpen(false)}
          onSuccess={() => {
            fetchStationeriesTable();
            fetchAllStationeriesForSelect();
          }}
          initialData={editingStationery}
        />
      )}

      {/* Invoice Modal for Add/Edit */}
      {isModalOpen && (
        <InvoiceModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setPreselectedStationeryId(null);
          }}
          onSuccess={fetchInvoices}
          initialData={editingInvoice}
          preselectedStationeryId={preselectedStationeryId}
          studentList={studentList}
          stationeryList={allStationeriesForSelect}
        />
      )}

      {/* Payment Verification Modal */}
      {isVerificationModalOpen && selectedVerification && (
        <PaymentVerificationModal
          verification={selectedVerification}
          isOpen={isVerificationModalOpen}
          onClose={() => {
            setIsVerificationModalOpen(false);
            setVerificationDefaultAction(null);
          }}
          defaultAction={verificationDefaultAction}
          onSuccess={() => {
            fetchVerifications();
            fetchInvoices();
          }}
        />
      )}

      {/* Table Direct Reject Modal */}
      {isVerificationRejectModalOpen && selectedVerification && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100 shrink-0">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h4 className="text-lg font-black text-slate-800">
                  Reject Payment
                </h4>
                <p className="text-xs text-slate-400 font-semibold">
                  Please provide a reason for rejection.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200/80 space-y-3">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                Rejection Reason
              </label>
              <textarea
                value={tableRejectionReason}
                onChange={(e) => setTableRejectionReason(e.target.value)}
                placeholder="e.g. Blurred receipt, insufficient funds..."
                className="w-full bg-white rounded-xl border border-slate-200 p-3 outline-none font-medium text-sm focus:border-rose-400 min-h-[100px] resize-none"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsVerificationRejectModalOpen(false)}
                disabled={isRejecting}
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processTableRejection}
                disabled={isRejecting || !tableRejectionReason.trim()}
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isRejecting ? (
                  "Rejecting..."
                ) : (
                  <>
                    <X size={16} /> Confirm Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Payment on Behalf Modal (Superadmin only) */}
      {isOnBehalfModalOpen &&
        (() => {
          const selectedRawStudent = rawStudentsWithParents.find(
            (s) => s.id === onBehalfSelectedStudentId,
          );
          const studentParents: any[] = selectedRawStudent?.parents || [];

          return (
            <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-5 animate-in zoom-in-95 duration-200 max-h-[92vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shrink-0">
                      <CreditCard size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-slate-800">
                        Post Payment on Behalf
                      </h4>
                      <p className="text-xs text-slate-400 font-semibold">
                        Record a parent's payment as superadmin
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsOnBehalfModalOpen(false);
                      resetOnBehalfModal();
                    }}
                    className="text-slate-400 hover:text-slate-600 p-1"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Step 1: Select Student — server-side searchable dropdown */}
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                      Step 1 — Select Student{" "}
                      <span className="text-rose-500">*</span>
                    </label>
                    <div ref={onBehalfStudentDropdownRef} className="relative">
                      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2 overflow-hidden w-full">
                          <User size={15} className="text-slate-400 shrink-0" />
                          <button
                            type="button"
                            onClick={() =>
                              setIsOnBehalfStudentDropdownOpen((o) => !o)
                            }
                            className="bg-transparent font-bold text-xs text-slate-800 outline-none flex items-center justify-between gap-2 w-full truncate text-left"
                          >
                            <span className="truncate">
                              {onBehalfSelectedStudentObj
                                ? `${onBehalfSelectedStudentObj.name}${onBehalfSelectedStudentObj.classroom?.name ? ` · ${onBehalfSelectedStudentObj.classroom.name}` : ""}`
                                : "Select a student..."}
                            </span>
                            <ChevronDown
                              size={14}
                              className="text-slate-400 shrink-0"
                            />
                          </button>
                        </div>
                        {onBehalfSelectedStudentId && (
                          <button
                            type="button"
                            onClick={() => {
                              setOnBehalfSelectedStudentId("");
                              setOnBehalfSelectedStudentObj(null);
                              setOnBehalfStudentInvoices([]);
                              setOnBehalfForm((f) => ({
                                ...f,
                                invoice_id: "",
                                parent_id: "",
                              }));
                            }}
                            className="text-slate-400 hover:text-slate-600 shrink-0 ml-1 p-0.5"
                          >
                            <X size={13} />
                          </button>
                        )}
                      </div>

                      {isOnBehalfStudentDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-2xl border border-slate-200 shadow-2xl z-50 p-3 space-y-2 max-h-64 flex flex-col animate-in fade-in zoom-in-95 duration-150">
                          <div className="relative flex items-center shrink-0">
                            <Search
                              size={15}
                              className="absolute left-3 text-slate-400"
                            />
                            <input
                              type="text"
                              autoFocus
                              placeholder="Search student..."
                              value={onBehalfStudentSearchQuery}
                              onChange={(e) =>
                                setOnBehalfStudentSearchQuery(e.target.value)
                              }
                              className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl pl-9 pr-7 py-2 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 placeholder:text-slate-400"
                            />
                            {fetchingOnBehalfStudents && (
                              <Loader2
                                size={13}
                                className="absolute right-2.5 text-emerald-600 animate-spin"
                              />
                            )}
                          </div>
                          <div className="overflow-y-auto flex-1 divide-y divide-slate-50 pr-1">
                            {onBehalfStudentList.length === 0 ? (
                              <div className="p-3 text-center text-xs text-slate-400 font-medium">
                                {fetchingOnBehalfStudents
                                  ? "Searching..."
                                  : onBehalfStudentSearchQuery
                                    ? `No students found for "${onBehalfStudentSearchQuery}"`
                                    : "Type to search students"}
                              </div>
                            ) : (
                              onBehalfStudentList.map((st: any) => {
                                const isSelected =
                                  onBehalfSelectedStudentId === st.id;
                                return (
                                  <button
                                    key={st.id}
                                    type="button"
                                    onClick={() => {
                                      setOnBehalfSelectedStudentId(st.id);
                                      setOnBehalfSelectedStudentObj(st);
                                      setIsOnBehalfStudentDropdownOpen(false);
                                      setOnBehalfForm((f) => ({
                                        ...f,
                                        invoice_id: "",
                                        parent_id: "",
                                      }));
                                    }}
                                    className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-colors ${isSelected ? "bg-emerald-50 text-emerald-700 font-bold" : "hover:bg-slate-50 text-slate-700"}`}
                                  >
                                    <div className="flex items-center gap-1.5 overflow-hidden">
                                      <span className="font-bold text-xs truncate">
                                        {st.name}
                                      </span>
                                      {st.classroom?.name && (
                                        <span className="bg-slate-100 text-slate-600 text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0">
                                          {st.classroom.name}
                                        </span>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <Check
                                        size={14}
                                        className="text-emerald-600 shrink-0"
                                      />
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step 2: Select Parent */}
                  {onBehalfSelectedStudentId && (
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                        Step 2 — Select Parent{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      {studentParents.length === 0 ? (
                        <p className="text-xs text-slate-400 font-medium italic">
                          No linked parents found for this student.
                        </p>
                      ) : (
                        <select
                          value={onBehalfForm.parent_id}
                          onChange={(e) =>
                            setOnBehalfForm((f) => ({
                              ...f,
                              parent_id: e.target.value,
                            }))
                          }
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                        >
                          <option value="">-- Select a parent --</option>
                          {studentParents.map((p: any) => (
                            <option
                              key={p.id || p.parent_id}
                              value={p.id || p.parent_id}
                            >
                              {p.name || p.fullname || "Parent"}
                              {p.relationship ? ` (${p.relationship})` : ""}
                              {p.phone ? ` · ${p.phone}` : ""}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Step 3: Select Invoice */}
                  {onBehalfSelectedStudentId && (
                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                        Step 3 — Select Invoice{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      {loadingOnBehalfInvoices ? (
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-medium py-1">
                          <Loader2 size={14} className="animate-spin" /> Loading
                          invoices...
                        </div>
                      ) : onBehalfStudentInvoices.length === 0 ? (
                        <p className="text-xs text-slate-400 font-medium italic">
                          No invoices found for this student.
                        </p>
                      ) : (
                        <select
                          value={onBehalfForm.invoice_id}
                          onChange={(e) =>
                            setOnBehalfForm((f) => ({
                              ...f,
                              invoice_id: e.target.value,
                            }))
                          }
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                        >
                          <option value="">-- Select an invoice --</option>
                          {onBehalfStudentInvoices.map((inv) => (
                            <option key={inv.id} value={inv.id}>
                              {inv.invoice_code || inv.id.substring(0, 8)} ·
                              Term {inv.term} {inv.academic_session} · ₦
                              {Number(inv.total_amount).toLocaleString()} · Bal:
                              ₦
                              {Number(
                                inv.remaining_balance ?? inv.total_amount,
                              ).toLocaleString()}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Payment Details */}
                  {(() => {
                    const selectedInv = onBehalfStudentInvoices.find(
                      (inv) => inv.id === onBehalfForm.invoice_id,
                    );
                    const invoiceBalance = Number(
                      selectedInv?.remaining_balance ??
                        selectedInv?.total_amount ??
                        0,
                    );
                    return (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              Amount Paid (₦){" "}
                              <span className="text-rose-500">*</span>
                            </label>
                            {selectedInv && invoiceBalance > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setOnBehalfForm((f) => ({
                                    ...f,
                                    amount_paid: String(invoiceBalance),
                                  }))
                                }
                                className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-wider bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors"
                              >
                                Pay All · ₦{invoiceBalance.toLocaleString()}
                              </button>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            value={onBehalfForm.amount_paid}
                            onChange={(e) =>
                              setOnBehalfForm((f) => ({
                                ...f,
                                amount_paid: e.target.value,
                              }))
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="e.g. 1536000"
                            className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                            Payment Method{" "}
                            <span className="text-rose-500">*</span>
                          </label>
                          <select
                            value={onBehalfForm.payment_method}
                            onChange={(e) =>
                              setOnBehalfForm((f) => ({
                                ...f,
                                payment_method: e.target.value,
                              }))
                            }
                            className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                          >
                            <option value="transfer">Transfer</option>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="cheque">Cheque</option>
                            <option value="pos">POS</option>
                          </select>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Proof of Payment Upload */}
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                      Payment Proof (Receipt / Screenshot)
                    </label>
                    <label className="flex flex-col items-center justify-center w-full cursor-pointer group">
                      {onBehalfProofPreview ? (
                        <div className="relative w-full rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                          <img
                            src={onBehalfProofPreview}
                            alt="Proof preview"
                            className="w-full max-h-40 object-contain p-2"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setOnBehalfProofFile(null);
                              setOnBehalfProofPreview("");
                            }}
                            className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1 shadow"
                          >
                            <X size={12} />
                          </button>
                          <p className="text-[10px] text-slate-500 font-bold text-center pb-2 truncate px-4">
                            {onBehalfProofFile?.name}
                          </p>
                        </div>
                      ) : (
                        <div className="w-full border-2 border-dashed border-slate-200 rounded-2xl p-6 flex flex-col items-center gap-2 bg-[#F8FAFC] group-hover:border-emerald-400 group-hover:bg-emerald-50/30 transition-all">
                          <UploadCloud
                            size={28}
                            className="text-slate-300 group-hover:text-emerald-500 transition-colors"
                          />
                          <p className="text-xs font-black text-slate-400 group-hover:text-emerald-600">
                            Click to upload receipt / proof
                          </p>
                          <p className="text-[10px] text-slate-300 font-medium">
                            PNG, JPG, JPEG, PDF · Max 9MB
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept={uploadAccept.image}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const validation = validateUploadFile(file, "image");
                          if (!validation.valid) {
                            toast.error(validation.message);
                            e.target.value = "";
                            return;
                          }
                          if (file.size >= 10_000_000) {
                            toast.error("File must be less than 9MB");
                            e.target.value = "";
                            return;
                          }
                          setOnBehalfProofFile(file);
                          setOnBehalfProofPreview(URL.createObjectURL(file));
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                      Admin Notes
                    </label>
                    <textarea
                      value={onBehalfForm.admin_notes}
                      onChange={(e) =>
                        setOnBehalfForm((f) => ({
                          ...f,
                          admin_notes: e.target.value,
                        }))
                      }
                      placeholder="e.g. Transfer received and confirmed by bursar"
                      rows={2}
                      className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 resize-none"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Transaction PIN <span className="text-rose-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsSetPinModalOpen(true)}
                        className="flex items-center gap-1 text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-wider bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors"
                      >
                        <KeyRound size={11} />{" "}
                        {isSuperAdmin
                          ? "Set / Change PIN"
                          : "Don't have a PIN?"}
                      </button>
                    </div>
                    <input
                      type="password"
                      value={onBehalfForm.transaction_pin}
                      onChange={(e) =>
                        setOnBehalfForm((f) => ({
                          ...f,
                          transaction_pin: e.target.value,
                        }))
                      }
                      placeholder="Enter your 6-digit PIN"
                      maxLength={6}
                      className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 tracking-widest"
                    />
                    <p className="text-[10px] text-slate-400 font-medium mt-1 ml-1">
                      No PIN yet? Click{" "}
                      <strong
                        className="text-blue-600 cursor-pointer"
                        onClick={() => setIsSetPinModalOpen(true)}
                      >
                        Set / Change PIN
                      </strong>{" "}
                      above to create one, or visit{" "}
                      <strong>Settings → Security</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOnBehalfModalOpen(false);
                      resetOnBehalfModal();
                    }}
                    disabled={isPostingOnBehalf}
                    className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePostOnBehalf}
                    disabled={
                      isPostingOnBehalf ||
                      uploadingProof ||
                      !onBehalfForm.invoice_id ||
                      !onBehalfForm.parent_id ||
                      !onBehalfForm.amount_paid ||
                      !onBehalfForm.transaction_pin
                    }
                    className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingProof ? (
                      <>
                        <Loader2 size={15} className="animate-spin" /> Uploading
                        proof...
                      </>
                    ) : isPostingOnBehalf ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />{" "}
                        Posting...
                      </>
                    ) : (
                      <>
                        <CreditCard size={15} /> Post Payment
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Set Transaction PIN Modal */}
      {isSetPinModalOpen && (
        <div className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 shrink-0">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h4 className="text-base font-black text-slate-800">
                    Set Transaction PIN
                  </h4>
                  <p className="text-[11px] text-slate-400 font-semibold">
                    Create or update your 6-digit PIN
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSetPinModalOpen(false);
                  setSetPinForm({
                    current_password: "",
                    transaction_pin: "",
                    transaction_pin_confirmation: "",
                  });
                }}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Current Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5">
                  <input
                    type={showSetPinPassword ? "text" : "password"}
                    value={setPinForm.current_password}
                    onChange={(e) =>
                      setSetPinForm((f) => ({
                        ...f,
                        current_password: e.target.value,
                      }))
                    }
                    placeholder="Your account password"
                    className="w-full bg-transparent text-xs font-bold text-slate-800 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSetPinPassword((v) => !v)}
                    className="text-slate-400 hover:text-slate-600 shrink-0 ml-1"
                  >
                    {showSetPinPassword ? (
                      <Eye size={14} />
                    ) : (
                      <EyeOff size={14} />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  New Transaction PIN <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  value={setPinForm.transaction_pin}
                  onChange={(e) =>
                    setSetPinForm((f) => ({
                      ...f,
                      transaction_pin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  placeholder="6-digit PIN"
                  maxLength={6}
                  className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 tracking-widest"
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Confirm PIN <span className="text-rose-500">*</span>
                </label>
                <input
                  type="password"
                  value={setPinForm.transaction_pin_confirmation}
                  onChange={(e) =>
                    setSetPinForm((f) => ({
                      ...f,
                      transaction_pin_confirmation: e.target.value.replace(
                        /\D/g,
                        "",
                      ),
                    }))
                  }
                  placeholder="Re-enter 6-digit PIN"
                  maxLength={6}
                  className={`w-full bg-[#F8FAFC] border rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 tracking-widest ${
                    setPinForm.transaction_pin_confirmation &&
                    setPinForm.transaction_pin !==
                      setPinForm.transaction_pin_confirmation
                      ? "border-rose-400"
                      : "border-slate-200"
                  }`}
                />
                {setPinForm.transaction_pin_confirmation &&
                  setPinForm.transaction_pin !==
                    setPinForm.transaction_pin_confirmation && (
                    <p className="text-[10px] text-rose-500 font-bold mt-1 ml-1">
                      PINs do not match
                    </p>
                  )}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => {
                  setIsSetPinModalOpen(false);
                  setSetPinForm({
                    current_password: "",
                    transaction_pin: "",
                    transaction_pin_confirmation: "",
                  });
                }}
                disabled={isSettingPin}
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSetPin}
                disabled={
                  isSettingPin ||
                  !setPinForm.current_password ||
                  !setPinForm.transaction_pin ||
                  setPinForm.transaction_pin !==
                    setPinForm.transaction_pin_confirmation
                }
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSettingPin ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <KeyRound size={14} /> Save PIN
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewInvoice && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg max-h-[85vh] p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200">
            {/* Header - Fixed top */}
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center border border-blue-100">
                  <FileText size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-800">
                    Invoice Details
                  </h3>
                  <p className="text-slate-400 font-bold text-xs">
                    {viewInvoice.student?.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewInvoice(null)}
                className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto my-4 pr-1 space-y-4">
              <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-slate-100 flex justify-between items-center">
                <div>
                  {viewInvoice.invoice_code && (
                    <span className="inline-block font-mono font-bold text-xs bg-slate-200 text-slate-800 px-2.5 py-0.5 rounded-lg mb-1">
                      {viewInvoice.invoice_code}
                    </span>
                  )}
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Session & Term
                  </span>
                  <p className="text-base font-black text-slate-800">
                    {viewInvoice.academic_session} (Term {viewInvoice.term})
                  </p>
                </div>
                {getStatusBadge(
                  viewInvoice.status,
                  viewInvoice.remaining_balance,
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100 text-center">
                <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                    Total
                  </span>
                  <span className="text-sm font-black text-slate-900">
                    ₦{Number(viewInvoice.total_amount || 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                    Paid
                  </span>
                  <span className="text-sm font-black text-emerald-600">
                    ₦{Number(viewInvoice.amount_paid || 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                    Remaining
                  </span>
                  <span className="text-sm font-black text-rose-600">
                    ₦
                    {Number(
                      viewInvoice.remaining_balance ?? viewInvoice.total_amount,
                    ).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Stationery Items / Line Items display */}
              {((viewInvoice.stationary_items &&
                viewInvoice.stationary_items.length > 0) ||
                (viewInvoice.line_items &&
                  viewInvoice.line_items.length > 0)) && (
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Stationery Items
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">
                      {
                        (viewInvoice.stationary_items || viewInvoice.line_items)
                          ?.length
                      }{" "}
                      item(s)
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100 bg-[#F8FAFC] rounded-2xl p-4 border border-slate-100 text-xs max-h-60 overflow-y-auto space-y-1">
                    {(
                      viewInvoice.stationary_items || viewInvoice.line_items
                    )?.map((item: any, idx: number) => {
                      const itemName =
                        item.stationary?.name ||
                        item.description ||
                        `Stationery Item #${idx + 1}`;
                      const unitPrice =
                        item.unit_price || item.stationary?.unit_price || 0;
                      const qty = item.quantity || 1;
                      const discPercent = item.discount_percentage
                        ? Number(item.discount_percentage)
                        : 0;
                      const grossTotal = qty * unitPrice;
                      const total =
                        item.amount !== undefined
                          ? item.amount
                          : grossTotal - (grossTotal * discPercent) / 100;
                      return (
                        <div
                          key={idx}
                          className="py-2 flex justify-between items-center"
                        >
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-800 block">
                                {itemName} ({qty}x)
                              </span>
                              {discPercent > 0 && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                  -{discPercent}% off
                                </span>
                              )}
                            </div>
                            {unitPrice > 0 && (
                              <span className="text-[10px] text-slate-400 font-medium">
                                ₦{unitPrice.toLocaleString()} each
                              </span>
                            )}
                          </div>
                          <span className="font-black text-slate-900">
                            ₦{Number(total).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewInvoice.notes && (
                <p className="text-xs text-slate-400 italic">
                  Note: {viewInvoice.notes}
                </p>
              )}
            </div>

            {/* Footer - Fixed bottom */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 shrink-0 bg-white z-10">
              {(() => {
                const isPaidOrPartial =
                  Number(viewInvoice.amount_paid || 0) > 0;
                return (
                  <button
                    type="button"
                    disabled={isPaidOrPartial}
                    onClick={() => handleDeleteInvoice(viewInvoice)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
                      isPaidOrPartial
                        ? "bg-slate-100 text-slate-400 opacity-40 cursor-not-allowed"
                        : "bg-rose-50 hover:bg-rose-100 text-rose-700 cursor-pointer"
                    }`}
                    title={
                      isPaidOrPartial
                        ? `Cannot delete invoice: ₦${Number(viewInvoice.amount_paid).toLocaleString()} already paid`
                        : "Delete Invoice"
                    }
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                );
              })()}
              <button
                type="button"
                onClick={() => {
                  const target = viewInvoice;
                  setViewInvoice(null);
                  setEditingInvoice(target);
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
              >
                <Edit3 size={16} /> Edit Invoice
              </button>
              <button
                type="button"
                onClick={() => setViewInvoice(null)}
                className="bg-[#0F172A] hover:bg-slate-800 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
